/**
 * LaViola.it - Notizie Fiorentina
 * Web App Entry Point
 * 
 * Simplified version of App.tsx without native-only features:
 * - No Firebase messaging / push notifications
 * - No InMobi CMP consent
 * - No Android PermissionsAndroid
 * - Shorter splash screen
 */

import React, {useState, useCallback} from 'react';
import {NavigationContainer, RouteProp, useRoute, createNavigationContainerRef} from '@react-navigation/native';
import {createNativeStackNavigator} from '@react-navigation/native-stack';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import {StatusBar, View, StyleSheet} from 'react-native';
import {HomeScreen} from './src/screens/HomeScreen';
import {ArticleScreen} from './src/screens/ArticleScreen';
import {LoginScreen} from './src/screens/LoginScreen';
import {RegisterScreen} from './src/screens/RegisterScreen';
import {ForgotPasswordScreen} from './src/screens/ForgotPasswordScreen';
import {SearchScreen} from './src/screens/SearchScreen';
import {DirettaScreen} from './src/screens/DirettaScreen';
import {CalendarioScreen} from './src/screens/CalendarioScreen';
import {ClassificaScreen} from './src/screens/ClassificaScreen';
import {ProfileScreen} from './src/screens/ProfileScreen';
import {SplashScreen} from './src/screens/SplashScreen';
import {FormazioneVotingScreen} from './src/screens/FormazioneVotingScreen';
import {FormazioneRisultatiScreen} from './src/screens/FormazioneRisultatiScreen';
import {PagelleScreen} from './src/screens/PagelleScreen';
import {BottomTabBar} from './src/components/BottomTabBar';
import {ErrorBoundary} from './src/components/ErrorBoundary';
import {AuthProvider} from './src/hooks/useAuth';
import {ThemeProvider} from './src/hooks/useTheme';

import {Article} from './src/types/article';
import {Match} from './src/types/diretta';
import {Colors} from './src/theme/colors';

import {TabNavigationProvider} from './src/hooks/useTabNavigation';

// Define navigation types
export type RootStackParamList = {
  Home: {tab?: string} | undefined;
  Article: {article: Article; index: number};
  Diretta: {match: Match; initialTab?: string};
  Login: undefined;
  Register: undefined;
  ForgotPassword: undefined;
  Search: undefined;
  FormazioneVoting: undefined;
  FormazioneRisultati: {matchId: string};
  Pagelle: {matchId: string};
  Profile: undefined;
};

const Stack = createNativeStackNavigator<RootStackParamList>();
const navigationRef = createNavigationContainerRef<RootStackParamList>();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Main tabs container
const MainTabs: React.FC = () => {
  const route = useRoute<RouteProp<RootStackParamList, 'Home'>>();
  const initialTab = route.params?.tab || 'home';
  const [activeTab, setActiveTab] = useState(initialTab);

  React.useEffect(() => {
    if (route.params?.tab) {
      setActiveTab(route.params.tab);
    }
  }, [route.params?.tab]);

  const renderScreen = () => {
    switch (activeTab) {
      case 'home':
        return <HomeScreen />;
      case 'calendario':
        return <CalendarioScreen />;
      case 'classifica':
        return <ClassificaScreen />;
      case 'profile':
        return <ProfileScreen />;
      default:
        return <HomeScreen />;
    }
  };

  return (
    <TabNavigationProvider 
      goHome={() => setActiveTab('home')}
      goToProfile={() => setActiveTab('profile')}
    >
      <View style={styles.container}>
        {renderScreen()}
        <BottomTabBar activeTab={activeTab} onTabPress={setActiveTab} />
      </View>
    </TabNavigationProvider>
  );
};

function App(): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(true);

  const handleFinish = useCallback(() => {
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <SafeAreaProvider>
        <StatusBar barStyle="light-content" backgroundColor={Colors.primary} />
        <SplashScreen onFinish={handleFinish} />
      </SafeAreaProvider>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <SafeAreaProvider>
              <StatusBar barStyle="light-content" backgroundColor={Colors.primary} />
              <NavigationContainer ref={navigationRef}>
                <Stack.Navigator
                  screenOptions={{
                    headerShown: false,
                  }}>
                  <Stack.Screen name="Home" component={MainTabs} />
                  <Stack.Screen name="Article" component={ArticleScreen} />
                  <Stack.Screen name="Diretta" component={DirettaScreen} />
                  <Stack.Screen name="FormazioneVoting" component={FormazioneVotingScreen} />
                  <Stack.Screen name="FormazioneRisultati" component={FormazioneRisultatiScreen} />
                  <Stack.Screen name="Pagelle" component={PagelleScreen} />
                  <Stack.Screen name="Search" component={SearchScreen} />
                  <Stack.Screen name="Login" component={LoginScreen} />
                  <Stack.Screen name="Register" component={RegisterScreen} />
                  <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} />
                  <Stack.Screen name="Profile" component={ProfileScreen} />
                </Stack.Navigator>
              </NavigationContainer>
            </SafeAreaProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default App;
